import { useTranslation } from 'react-i18next';

interface GuideData {
  tagKey: string;
  heading1Key: string;
  heading2Key: string;
  heading3Key: string;
  descKey: string;
  quoteKey: string;
  authorKey: string;
  ctaKey: string;
  linkPath: string;
  reversed: boolean;
}

export default function GuideSection() {
  const { t } = useTranslation();

  const guides: GuideData[] = [
    {
      tagKey: 'shipping.title',
      heading1Key: 'shipping.heading1',
      heading2Key: 'shipping.heading2',
      heading3Key: 'shipping.heading3',
      descKey: 'shipping.desc',
      quoteKey: 'shipping.quote',
      authorKey: 'shipping.author',
      ctaKey: 'shipping.cta',
      linkPath: '/artikel/oopbuy-spreadsheet-guide',
      reversed: false,
    },
    {
      tagKey: 'qc.title',
      heading1Key: 'qc.heading1',
      heading2Key: 'qc.heading2',
      heading3Key: 'qc.heading3',
      descKey: 'qc.desc',
      quoteKey: 'qc.quote',
      authorKey: 'qc.author',
      ctaKey: 'qc.cta',
      linkPath: '/artikel/oopbuy-spreadsheet-guide',
      reversed: true,
    },
    {
      tagKey: 'safety.title',
      heading1Key: 'safety.heading1',
      heading2Key: 'safety.heading2',
      heading3Key: 'safety.heading3',
      descKey: 'safety.desc',
      quoteKey: 'safety.quote',
      authorKey: 'safety.author',
      ctaKey: 'safety.cta',
      linkPath: '/artikel/oopbuy-spreadsheet-guide',
      reversed: false,
    },
    {
      tagKey: 'beginner.title',
      heading1Key: 'beginner.heading1',
      heading2Key: 'beginner.heading2',
      heading3Key: 'beginner.heading3',
      descKey: 'beginner.desc',
      quoteKey: 'beginner.quote',
      authorKey: 'beginner.author',
      ctaKey: 'beginner.cta',
      linkPath: '/artikel/oopbuy-spreadsheet-guide',
      reversed: true,
    },
  ];

  return (
    <>
      {guides.map((guide, index) => (
        <section
          key={index}
          className={`py-16 md:py-24 ${index % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}`}
        >
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <div className={`flex flex-col ${guide.reversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 lg:gap-16 items-center`}>
              {/* Text side */}
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                  {t(guide.tagKey)}
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                  {t(guide.heading1Key)}
                  <br />
                  {t(guide.heading2Key)}
                  <br />
                  {t(guide.heading3Key)}
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-md leading-relaxed mb-6">
                  {t(guide.descKey)}
                </p>
                <a
                  href={guide.linkPath}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-foreground-200 text-foreground-900 text-sm font-medium hover:bg-foreground-900 hover:text-background-50 hover:border-foreground-900 transition-all cursor-pointer whitespace-nowrap"
                >
                  {t(guide.ctaKey)}
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>

              {/* Card side */}
              <div className="flex-1">
                <div className="bg-accent-600 rounded-[32px] p-8 md:p-10 relative overflow-hidden">
                  {/* Decorative dots */}
                  <div className="absolute top-6 right-6 flex gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-background-50/20"></span>
                    <span className="w-2 h-2 rounded-full bg-background-50/30"></span>
                    <span className="w-2 h-2 rounded-full bg-background-50/20"></span>
                  </div>
                  <div className="absolute top-6 left-6">
                    <span className="text-xs text-background-50/40 font-medium uppercase tracking-wider">
                      {t(guide.tagKey)}
                    </span>
                  </div>

                  <div className="mt-10">
                    <p className="text-base md:text-lg text-background-50/90 leading-relaxed mb-8">
                      {t(guide.quoteKey)}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-400/40 flex items-center justify-center">
                        <i className="ri-user-line text-background-50/80"></i>
                      </div>
                      <span className="text-sm text-background-50/70">{t(guide.authorKey)}</span>
                    </div>
                    <a
                      href={guide.linkPath}
                      className="px-4 py-2 rounded-full bg-background-50/10 text-background-50 text-sm border border-background-50/20 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                    >
                      {t(guide.ctaKey)}
                      <i className="ri-arrow-right-line text-xs"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </>
  );
}